import React from 'react';
import styled from '@emotion/styled';
import { CircleStop, Rocket, Copy, Check } from 'lucide-react';
import {
  Card, PageHeader, PageTitle, Table, Thead, Tbody, Mono, DestructiveButton, Badge,
} from '../styled-components';
import { EmptyState, SkeletonRows, DirectionFilter, type DirectionFilterValue } from '../components';
import { useFetchData, showToast } from '../hooks';
import { fetchOrThrow, getCSRFToken, CSRF_HEADER_NAME } from '../../common/utils';

type CallDirection = 'inbound' | 'outbound';
type DeploymentState = 'started' | 'deleted' | 'paused';
type RuntimeState = 'pending' | 'running' | 'completed' | 'error';

interface RuntimeStatus {
  state: RuntimeState | (string & {});
  reason: string | null;
  ready_replicas: number | null;
  replicas: number | null;
}

interface Deployment {
  task_id: string;
  project_id: string;
  project_name: string | null;
  build_id: string;
  state: DeploymentState | (string & {});
  call_direction: CallDirection | null;
  created_at: string;
  runtime: RuntimeStatus | null;
}

const CopyWrap = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
});

const CopyBtn = styled.button({
  display: 'inline-flex',
  alignItems: 'center',
  background: 'none',
  border: 'none',
  cursor: 'pointer',
  color: 'var(--text-muted)',
  padding: 4,
  borderRadius: 3,
  transition: 'color 150ms, transform 150ms',
  '&:hover': { color: 'var(--accent-cyan)' },
  '&:active': { transform: 'scale(0.9)' },
});

function CopyButton({ text }: { text: string }) {
  const [copied, setCopied] = React.useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    });
  };
  return (
    <CopyBtn onClick={handleCopy} title="Copy">
      {copied ? <Check size={14} /> : <Copy size={14} />}
    </CopyBtn>
  );
}

function stateBadge(d: Deployment): { variant: string; label: string; title?: string } {
  // Mongo `state` is user intent; controller `runtime.state` is the live reality.
  // Show runtime when it disagrees with intent (started but erroring/pending).
  if (d.state === 'deleted') return { variant: 'deactivated', label: 'stopped' };
  if (d.state === 'started') {
    const rt = d.runtime;
    if (!rt) return { variant: 'paused', label: 'starting' };
    switch (rt.state) {
      case 'running':
        return { variant: 'active', label: 'running' };
      case 'error':
        return { variant: 'deactivated', label: 'error', title: rt.reason ?? undefined };
      case 'pending':
        return { variant: 'paused', label: 'pending', title: rt.reason ?? undefined };
      case 'completed':
        return { variant: 'default', label: 'completed' };
      default:
        return { variant: 'default', label: rt.state };
    }
  }
  return { variant: 'default', label: d.state || 'unknown' };
}

const FilterBar = styled.div({
  display: 'flex',
  gap: 10,
  marginBottom: 16,
  flexWrap: 'wrap',
});

const EMPTY_COPY: Record<DirectionFilterValue, { title: string; body: string }> = {
  all: {
    title: 'No deployments yet',
    body: 'Use the Guava CLI to deploy your agent. Deployments will appear here.',
  },
  inbound: {
    title: 'No inbound deployments yet',
    body: 'Use the Guava CLI to deploy your agent. Inbound deployments will appear here.',
  },
  outbound: {
    title: 'No outbound deployments yet',
    body: 'Outbound deployments will appear here.',
  },
};

export function DeploymentsPage() {
  const { data, loading, refetch } = useFetchData<Deployment[]>('/dashboard/api/deployments', []);
  const [deleting, setDeleting] = React.useState<string | null>(null);
  const [direction, setDirection] = React.useState<DirectionFilterValue>('all');

  async function handleDelete(taskId: string) {
    if (deleting) return;
    setDeleting(taskId);
    try {
      await fetchOrThrow(`/dashboard/api/deployments/${taskId}`, {
        method: 'DELETE',
        headers: { [CSRF_HEADER_NAME]: await getCSRFToken() },
      });
      showToast('Deployment stopped.', 'success');
      refetch();
    } catch (err) {
      showToast((err as Error)?.message ?? 'Failed to stop deployment.', 'error');
    } finally {
      setDeleting(null);
    }
  }

  // Unknown (null call_direction) only appears under "all" — it's being phased out.
  const rows = data.filter((d) => {
    if (direction === 'all') return true;
    return d.call_direction === direction;
  });
  const empty = EMPTY_COPY[direction];

  return (
    <div>
      <PageHeader>
        <PageTitle>Deployments</PageTitle>
      </PageHeader>

      <FilterBar>
        <DirectionFilter value={direction} onChange={setDirection} />
      </FilterBar>

      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <Table>
          <Thead>
            <tr>
              <th>Name</th>
              <th>Status</th>
              <th>Replicas</th>
              <th>ID</th>
              <th></th>
            </tr>
          </Thead>
          <Tbody>
            {loading ? (
              <SkeletonRows cols={5} />
            ) : rows.length === 0 ? (
              <tr><td colSpan={5}>
                <EmptyState
                  icon={<Rocket size={40} />}
                  title={empty.title}
                  body={empty.body}
                />
              </td></tr>
            ) : rows.map((d) => {
              const badge = stateBadge(d);
              const isInactive = d.state === 'deleted';
              const rt = d.runtime;
              const replicas = !isInactive && rt && rt.replicas !== null && rt.ready_replicas !== null
                ? `${rt.ready_replicas}/${rt.replicas}`
                : '—';
              return (
                <tr key={d.task_id} style={isInactive ? { opacity: 0.6 } : undefined}>
                  <td>{d.project_name ?? d.project_id ?? 'unknown'}</td>
                  <td><Badge variant={badge.variant} title={badge.title}>{badge.label}</Badge></td>
                  <td style={{ color: 'var(--text-secondary)', fontSize: 13 }}>
                    {replicas}
                  </td>
                  <td>
                    <CopyWrap>
                      <Mono style={{ color: 'var(--text-secondary)', fontSize: 12 }}>{d.task_id}</Mono>
                      <CopyButton text={d.task_id} />
                    </CopyWrap>
                  </td>
                  <td onClick={(e) => e.stopPropagation()}>
                    {!isInactive && (
                      <DestructiveButton
                        style={{ padding: '5px 10px', fontSize: 13 }}
                        onClick={() => handleDelete(d.task_id)}
                        disabled={deleting === d.task_id}
                      >
                        <CircleStop size={14} /> Stop
                      </DestructiveButton>
                    )}
                  </td>
                </tr>
              );
            })}
          </Tbody>
        </Table>
      </Card>
    </div>
  );
}